/**
 * CampaignDetail - Tabbed detail view for campaigns
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@respectly/ui';
import {
  ArrowLeft,
  Edit2,
  Play,
  Pause,
  Trash2,
  Zap,
  Users,
  CheckCircle,
  TrendingUp,
  Clock,
  Loader2,
  BarChart3,
  GitBranch,
  List,
  AlertCircle,
} from 'lucide-react';
import { useCampaignsStore } from '../../stores/useCampaignsStore';
import {
  CampaignStatusBadge,
  CampaignTriggerBadge,
  CampaignTimeline,
  CampaignTimelineCompact,
  EnrollmentsList,
  CampaignStats,
} from '../../components/campaigns';

// ============================================
// Tab Configuration
// ============================================

const tabs = [
  { id: 'overview', label: 'Overview', icon: Zap },
  { id: 'timeline', label: 'Timeline', icon: GitBranch },
  { id: 'enrollments', label: 'Enrollments', icon: List },
  { id: 'stats', label: 'Statistics', icon: BarChart3 },
] as const;

type TabId = (typeof tabs)[number]['id'];

// ============================================
// Component
// ============================================

export function CampaignDetail() {
  const navigate = useNavigate();
  const params = useParams({ strict: false });
  const campaignId = (params as { id?: string; tab?: string }).id;
  const tabParam = (params as { id?: string; tab?: string }).tab as TabId | undefined;

  const [activeTab, setActiveTab] = useState<TabId>(tabParam || 'overview');

  const {
    activeCampaign,
    campaignStats,
    enrollments,
    isLoadingCampaign,
    isLoadingStats,
    isLoadingEnrollments,
    isPerformingAction,
    loadCampaign,
    loadStats,
    loadEnrollments,
    activateCampaign,
    pauseCampaign,
    deleteCampaign,
    clearActiveCampaign,
  } = useCampaignsStore();

  useEffect(() => {
    if (campaignId) {
      loadCampaign(campaignId);
      loadStats(campaignId);
      loadEnrollments(campaignId);
    }
    return () => clearActiveCampaign();
  }, [campaignId, loadCampaign, loadStats, loadEnrollments, clearActiveCampaign]);

  useEffect(() => {
    if (activeTab && activeTab !== 'overview' && campaignId) {
      navigate({ to: '/campaigns/$id/$tab', params: { id: campaignId, tab: activeTab }, replace: true });
    } else if (activeTab === 'overview' && tabParam && campaignId) {
      navigate({ to: '/campaigns/$id', params: { id: campaignId }, replace: true });
    }
  }, [activeTab, campaignId, navigate, tabParam]);

  const campaign = activeCampaign;

  const handleEdit = () => {
    if (campaignId) navigate({ to: '/campaigns/$id/edit', params: { id: campaignId } });
  };

  const handleActivate = async () => {
    if (campaignId) await activateCampaign(campaignId);
  };

  const handlePause = async () => {
    if (campaignId) await pauseCampaign(campaignId);
  };

  const handleDelete = async () => {
    if (campaignId && campaign && confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
      await deleteCampaign(campaignId);
      navigate({ to: '/campaigns' });
    }
  };

  if (isLoadingCampaign) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Not found state
  if (!campaign && !isLoadingCampaign) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-slate-300 dark:text-slate-600 mb-4" />
        <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">Campaign not found</h2>
        <p className="text-sm text-slate-500 mb-4">This campaign may have been deleted or doesn't exist.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/campaigns' })}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Campaigns
        </Button>
      </div>
    );
  }

  if (!campaign) return null;

  const completionRate =
    campaign.enrolledCount > 0
      ? Math.round((campaign.completedCount / campaign.enrolledCount) * 100)
      : 0;

  const exitRate =
    campaign.enrolledCount > 0
      ? Math.round((campaign.exitedCount / campaign.enrolledCount) * 100)
      : 0;

  const inProgress = campaign.enrolledCount - campaign.completedCount - campaign.exitedCount;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col h-full">
      {/* Header */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <div className="flex items-center justify-between p-4 lg:p-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate({ to: '/campaigns' })}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Campaigns
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                  {campaign.name}
                </h1>
                <CampaignStatusBadge status={campaign.status} />
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CampaignTriggerBadge trigger={campaign.trigger} size="sm" />
                <span>·</span>
                <span>{campaign.steps.length} steps</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {campaign.status === 'active' ? (
              <Button variant="outline" size="sm" onClick={handlePause} disabled={isPerformingAction}>
                <Pause className="h-4 w-4 mr-2" />
                Pause
              </Button>
            ) : campaign.status !== 'completed' && campaign.status !== 'archived' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={handleActivate}
                disabled={isPerformingAction}
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              >
                <Play className="h-4 w-4 mr-2" />
                Activate
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={handleEdit}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={isPerformingAction}
              className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 dark:border-red-800"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="px-4 lg:px-6 pb-4">
          <div className="flex gap-1 rounded-lg bg-slate-100 dark:bg-slate-800 p-1 w-fit">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                    activeTab === tab.id
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 lg:p-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Enrolled"
                value={campaign.enrolledCount}
                icon={Users}
                color="blue"
              />
              <StatCard
                label="In Progress"
                value={inProgress}
                icon={Clock}
                color="amber"
              />
              <StatCard
                label="Completed"
                value={campaign.completedCount}
                icon={CheckCircle}
                color="emerald"
              />
              <StatCard
                label="Completion Rate"
                value={`${completionRate}%`}
                icon={TrendingUp}
                color="blue"
              />
            </div>

            {/* Enrollment Funnel */}
            {campaign.enrolledCount > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Enrollment Funnel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <FunnelBar
                    label="Enrolled"
                    count={campaign.enrolledCount}
                    total={campaign.enrolledCount}
                    color="bg-blue-500"
                  />
                  <FunnelBar
                    label="In Progress"
                    count={inProgress}
                    total={campaign.enrolledCount}
                    color="bg-amber-400"
                  />
                  <FunnelBar
                    label="Completed"
                    count={campaign.completedCount}
                    total={campaign.enrolledCount}
                    color="bg-emerald-500"
                  />
                  {campaign.exitedCount > 0 && (
                    <FunnelBar
                      label="Exited"
                      count={campaign.exitedCount}
                      total={campaign.enrolledCount}
                      color="bg-red-400"
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Details + Dates */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Campaign Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <DetailRow label="Trigger">
                    <CampaignTriggerBadge trigger={campaign.trigger} />
                  </DetailRow>
                  <DetailRow
                    label="Audience"
                    value={
                      campaign.audienceType === 'all'
                        ? 'All accounts'
                        : campaign.audienceType === 'plans'
                        ? `${campaign.audiencePlans?.length || 0} plans`
                        : campaign.audienceType === 'tags'
                        ? `${campaign.audienceTags?.length || 0} tags`
                        : 'Custom'
                    }
                  />
                  <DetailRow label="Steps" value={`${campaign.steps.length} steps`} />
                  <DetailRow label="Exit Rate" value={`${exitRate}%`} />
                  {campaign.description && (
                    <div className="pt-1">
                      <span className="text-xs text-slate-500 block mb-1">Description</span>
                      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                        {campaign.description}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Timeline
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <DetailRow
                    label="Created"
                    value={new Date(campaign.createdAt).toLocaleDateString()}
                  />
                  {campaign.startDate && (
                    <DetailRow
                      label="Started"
                      value={new Date(campaign.startDate).toLocaleDateString()}
                    />
                  )}
                  {campaign.endDate && (
                    <DetailRow
                      label="Ends"
                      value={new Date(campaign.endDate).toLocaleDateString()}
                    />
                  )}
                  <DetailRow
                    label="Last Updated"
                    value={new Date(campaign.updatedAt).toLocaleDateString()}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Campaign Flow */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                  Campaign Flow
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CampaignTimelineCompact steps={campaign.steps} />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Timeline Tab */}
        {activeTab === 'timeline' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card>
              <CardHeader>
                <CardTitle>Campaign Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <CampaignTimeline steps={campaign.steps} />
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Enrollments Tab */}
        {activeTab === 'enrollments' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <EnrollmentsList
              enrollments={enrollments}
              steps={campaign.steps}
              isLoading={isLoadingEnrollments}
            />
          </motion.div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <CampaignStats
              stats={campaignStats}
              steps={campaign.steps}
              isLoading={isLoadingStats}
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// Sub components
// ============================================

const colorMap = {
  blue: { bg: 'bg-blue-100 dark:bg-blue-900/30', icon: 'text-blue-600 dark:text-blue-400' },
  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400' },
  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400' },
};

function StatCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string;
  value: number | string;
  icon: typeof Users;
  color: 'blue' | 'amber' | 'emerald';
}) {
  const colors = colorMap[color];
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center', colors.bg)}>
            <Icon className={cn('h-5 w-5', colors.icon)} />
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums leading-tight">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: string;
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-slate-500 w-20 flex-shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all', color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs font-medium text-slate-700 dark:text-slate-300 tabular-nums w-14 text-right">
        {count.toLocaleString()} <span className="text-slate-400">({Math.round(pct)}%)</span>
      </span>
    </div>
  );
}

function DetailRow({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm text-slate-500 dark:text-slate-400">{label}</span>
      {children ?? (
        <span className="text-sm font-medium text-slate-900 dark:text-white">{value}</span>
      )}
    </div>
  );
}
